import React, { useState, useRef } from 'react';
import { Upload, Trash2, GripVertical } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { resolveImageUrl } from '@/utils/image';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const GalleryTab = ({ photos: initialPhotos, categories, onRefresh }) => {
  const [photos, setPhotos] = useState(initialPhotos);
  const [newPhoto, setNewPhoto] = useState({ file: null, title: '', category: '' });
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  React.useEffect(() => { setPhotos(initialPhotos); }, [initialPhotos]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewPhoto({ ...newPhoto, file });
      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrl(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleAddPhoto = async (e) => {
    e.preventDefault();
    if (!newPhoto.file) { toast.error('Please select an image file'); return; }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', newPhoto.file);
      if (newPhoto.title) formData.append('title', newPhoto.title);
      if (newPhoto.category) formData.append('category', newPhoto.category);
      await axios.post(`${API}/gallery`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Photo added successfully');
      setNewPhoto({ file: null, title: '', category: '' });
      setPreviewUrl(null);
      onRefresh();
    } catch { toast.error('Failed to add photo'); }
    finally { setLoading(false); }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;
    try {
      await axios.delete(`${API}/gallery/${photoId}`);
      toast.success('Photo deleted successfully');
      onRefresh();
    } catch { toast.error('Failed to delete photo'); }
  };

  const handleDragStart = (index) => {
    dragItem.current = index;
  };

  const handleDragEnter = (index) => {
    dragOverItem.current = index;
  };

  const handleDragEnd = async () => {
    if (dragItem.current === null || dragOverItem.current === null || dragItem.current === dragOverItem.current) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }
    const newPhotos = [...photos];
    const dragged = newPhotos.splice(dragItem.current, 1)[0];
    newPhotos.splice(dragOverItem.current, 0, dragged);
    setPhotos(newPhotos);
    dragItem.current = null;
    dragOverItem.current = null;

    try {
      await axios.put(`${API}/gallery/reorder`, { photo_ids: newPhotos.map(p => p.id) });
      toast.success('Gallery order updated');
    } catch {
      toast.error('Failed to save order');
      onRefresh();
    }
  };

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
        <div className="border border-white/10 p-8">
          <h2 className="text-2xl font-normal mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>Add New Photo</h2>
          <form onSubmit={handleAddPhoto} className="space-y-6" data-testid="add-photo-form">
            <div>
              <label className="block text-sm uppercase tracking-widest mb-2">Select Image *</label>
              <input type="file" accept="image/*" onChange={handleFileChange} className="w-full bg-black border border-white/10 rounded-none px-4 py-3 text-white focus:outline-none focus:border-white/30 transition-colors file:mr-4 file:py-2 file:px-4 file:rounded-none file:border-0 file:text-sm file:font-semibold file:bg-white file:text-black hover:file:bg-gray-200" required data-testid="image-file-input" />
              {previewUrl && (<div className="mt-4 border border-white/10"><img src={previewUrl} alt="Preview" className="w-full h-48 object-cover" /></div>)}
            </div>
            <div>
              <label className="block text-sm uppercase tracking-widest mb-2">Title (Optional)</label>
              <input type="text" value={newPhoto.title} onChange={(e) => setNewPhoto({ ...newPhoto, title: e.target.value })} className="w-full bg-black border border-white/10 rounded-none px-4 py-3 text-white focus:outline-none focus:border-white/30 transition-colors" placeholder="Photo title" data-testid="photo-title-input" />
            </div>
            <div>
              <label className="block text-sm uppercase tracking-widest mb-2">Category (Optional)</label>
              <select value={newPhoto.category} onChange={(e) => setNewPhoto({ ...newPhoto, category: e.target.value })} className="w-full bg-black border border-white/10 rounded-none px-4 py-3 text-white focus:outline-none focus:border-white/30 transition-colors" data-testid="photo-category-select">
                <option value="">Select category</option>
                {categories.map((cat) => (<option key={cat.id} value={cat.name.toLowerCase()}>{cat.name}</option>))}
              </select>
            </div>
            <button type="submit" disabled={loading} className="w-full bg-white text-black hover:bg-gray-200 rounded-none px-8 py-4 text-sm uppercase tracking-widest transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="add-photo-button">
              <Upload size={16} />{loading ? 'Adding...' : 'Add Photo'}
            </button>
          </form>
        </div>
        <div className="border border-white/10 p-8">
          <h2 className="text-2xl font-normal mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>Instructions</h2>
          <div className="space-y-4 text-sm text-gray-400 leading-relaxed">
            <p>Select an image file from your computer (JPG, PNG, WebP)</p>
            <p>Preview will appear after selecting an image</p>
            <p>For best results, use high-quality images</p>
            <p>Drag and drop photos below to reorder the gallery</p>
            <p>You can delete photos by hovering and clicking the trash icon</p>
          </div>
        </div>
      </div>
      <div>
        <h2 className="text-2xl font-normal mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Current Gallery ({photos.length} photos)</h2>
        <p className="text-sm text-gray-400 mb-6">Drag photos to reorder how they appear in the gallery</p>
        {photos.length === 0 ? (
          <div className="text-center py-20 border border-white/10 rounded-none"><p className="text-gray-400">No photos in gallery yet</p></div>
        ) : (
          <div className="space-y-2" data-testid="gallery-reorder-list">
            {photos.map((photo, index) => (
              <div
                key={photo.id}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragEnter={() => handleDragEnter(index)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => e.preventDefault()}
                className="flex items-center gap-4 border border-white/10 p-3 bg-black hover:bg-white/5 cursor-grab active:cursor-grabbing transition-colors group"
                data-testid={`gallery-drag-item-${index}`}
              >
                <div className="text-gray-600 shrink-0 cursor-grab">
                  <GripVertical size={20} />
                </div>
                <span className="text-xs text-gray-600 w-6 shrink-0">{index + 1}</span>
                <img src={resolveImageUrl(photo.image_url)} alt={photo.title || 'Gallery photo'} className="w-16 h-12 object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{photo.title || 'Untitled'}</p>
                  {photo.category && <p className="text-xs text-gray-500">{photo.category}</p>}
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDeletePhoto(photo.id); }}
                  className="text-gray-600 hover:text-red-400 transition-colors shrink-0 p-2 opacity-0 group-hover:opacity-100"
                  data-testid={`delete-photo-${photo.id}`}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default GalleryTab;
